import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import { 
  Elements, 
  PaymentElement, 
  useStripe, 
  useElements 
} from '@stripe/react-stripe-js';
import { config } from '../../config';
import { authService } from '../../services/auth';

// Stripe publishable key from environment variables
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid #333;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px;
  margin-left: 16px;
  
  &:hover {
    color: white;
  }
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const UpdateButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5a67d8, #68637a);
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.9rem;
  margin-top: 12px;
  padding: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

interface UpdatePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PaymentForm: React.FC<{ 
  onSuccess: () => void; 
  onClose: () => void;
}> = ({ onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Confirm the setup intent to collect payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/subscription`,
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        setError(confirmError.message || 'Failed to confirm payment method');
        setIsProcessing(false);
        return;
      }

      if (setupIntent && setupIntent.payment_method) {
        // Update the payment method on the backend
        const token = authService.getToken();
        const response = await fetch(`${config.apiUrl}/stripe/subscription/update-payment-method`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            paymentMethodId: setupIntent.payment_method
          })
        });

        if (response.ok) {
          onSuccess();
        } else {
          const data = await response.json();
          setError(data.error || 'Failed to update payment method');
        }
      }
    } catch (err) {
      console.error('Error updating payment method:', err);
      setError('An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormSection>
        <PaymentElement 
          options={{
            layout: 'tabs'
          }}
        />
      </FormSection>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <UpdateButton type="submit" disabled={!stripe || isProcessing}>
        {isProcessing && <LoadingSpinner />}
        {isProcessing ? 'Updating...' : 'Update Payment Method'}
      </UpdateButton>
    </form>
  );
};

export const UpdatePaymentModal: React.FC<UpdatePaymentModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchSetupIntent();
    }
  }, [isOpen]);

  const fetchSetupIntent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = authService.getToken();
      const response = await fetch(`${config.apiUrl}/stripe/create-setup-intent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setClientSecret(data.clientSecret);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to initialize payment update');
      }
    } catch (err) {
      console.error('Error fetching setup intent:', err);
      setError('Failed to connect to payment service');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    onSuccess();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Update Payment Method</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <Description>
          Update your payment method for future subscription renewals. Your new payment method will be used for all future charges.
        </Description>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <LoadingSpinner style={{ width: '32px', height: '32px', borderWidth: '3px' }} />
            <p style={{ color: '#999', marginTop: '16px' }}>Loading payment form...</p>
          </div>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : clientSecret ? (
          <Elements 
            stripe={stripePromise} 
            options={{
              clientSecret: clientSecret,
              appearance: {
                theme: 'night',
                variables: {
                  colorPrimary: '#667eea',
                  colorBackground: '#1a1a1a',
                  colorText: '#ffffff',
                  colorDanger: '#ef4444',
                  borderRadius: '8px',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }
              }
            }}
          >
            <PaymentForm onSuccess={handleSuccess} onClose={onClose} />
          </Elements>
        ) : null}
      </ModalContent>
    </ModalOverlay>
  );
};